import { motion } from "framer-motion";
import { IMAGES } from "../../data/site";
import { Reveal, Kicker } from "./Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../ui/accordion";

const CHAPTERS = [
  { n: "01", t: "Our Mission", d: "Deliver exceptional workmanship, honest communication and dependable service on every project — no exceptions." },
  { n: "02", t: "Our Craft", d: "Skilled, professional tradespeople who sweat the details most contractors skip, because the finish is everything." },
  { n: "03", t: "Our Promise", d: "Transparent pricing, reliable scheduling and long-term relationships built one satisfied customer at a time." },
];

const FAQ = [
  { q: "Are you licensed and insured?", a: "Yes. Fast Fix Charlotte is fully licensed and insured for both residential and commercial work throughout the Charlotte, NC area." },
  { q: "How does the free estimate work?", a: "Share a few project details and we'll respond within one business day with a clear, itemized quote — no pressure and no obligation." },
  { q: "Do you handle both small repairs and full renovations?", a: "Absolutely. From a single TV mount to complete kitchen and bathroom upgrades, one trusted team handles it all." },
  { q: "Do you offer recurring commercial maintenance?", a: "Yes — we build recurring maintenance programs for offices, retail and property managers, scheduled around your operations." },
  { q: "Which areas do you serve?", a: "Charlotte and surrounding communities including Matthews, Mint Hill, Pineville, Ballantyne, Huntersville, Concord, Indian Trail, Fort Mill and Gastonia." },
  { q: "Is my satisfaction guaranteed?", a: "It is. We're not finished until you're genuinely happy with the result." },
];

export const About = () => (
  <section id="about" className="bg-ffc-light py-24 lg:py-32" data-testid="about">
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
      <div className="grid gap-14 lg:grid-cols-2">
        <div>
          <Reveal><Kicker>About Us</Kicker></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display mt-4 text-4xl font-extrabold sm:text-5xl lg:text-6xl">
              A locally owned team that Charlotte counts on.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-ffc-gray">
              Fast Fix Charlotte is a locally owned property solutions company proudly serving
              Charlotte, North Carolina and surrounding communities. We specialize in high-quality
              residential and commercial repairs, renovations, installations and maintenance —
              building long-term relationships by treating every project with care and professionalism.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="hover-zoom clip-frame aspect-[3/4] h-full min-h-[420px] overflow-hidden rounded-2xl bg-ffc-blue lg:aspect-auto">
            <img src={IMAGES.aboutTeam} alt="Fast Fix Charlotte team on the job — residential and commercial property solutions in Charlotte, NC" loading="lazy" className="h-full w-full object-cover object-center" />
          </div>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
        {CHAPTERS.map((c, i) => (
          <motion.div key={c.n} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="rounded-2xl border-t-2 border-ffc-red bg-white p-8" data-testid={`chapter-${i}`}>
            <span className="font-display text-4xl font-extrabold text-ffc-blue">{c.n}</span>
            <h3 className="font-display mt-3 text-xl font-bold">{c.t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-ffc-gray">{c.d}</p>
          </motion.div>
        ))}
      </div>

      <div id="faq" className="mt-20 grid gap-10 lg:grid-cols-[0.8fr_1.2fr]" data-testid="faq">
        <Reveal>
          <div>
            <Kicker>FAQ</Kicker>
            <h2 className="font-display mt-4 text-3xl font-extrabold sm:text-4xl">
              Questions, answered.
            </h2>
            <p className="mt-4 text-ffc-gray">Everything you need to know before we get started.</p>
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <Accordion type="single" collapsible className="rounded-2xl bg-white px-6">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg font-bold hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ffc-gray">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </div>
  </section>
);
